import React, { useState } from 'react';
import { Button } from 'primereact/button';
import './Dialog.css';

export type DialogType = 'alert' | 'confirm' | 'prompt';
export type DialogVariant = 'info' | 'success' | 'warning' | 'danger';

interface DialogProps {
  isOpen: boolean;
  type: DialogType;
  variant?: DialogVariant;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  placeholder?: string;
  defaultValue?: string;
  onConfirm: (value?: string) => void;
  onCancel: () => void;
}

const Dialog: React.FC<DialogProps> = ({
  isOpen,
  type,
  variant = 'info',
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  placeholder = '',
  defaultValue = '',
  onConfirm,
  onCancel
}) => {
  const [inputValue, setInputValue] = useState(defaultValue);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (type === 'prompt') {
      onConfirm(inputValue);
    } else {
      onConfirm();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && type !== 'prompt') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const getIcon = () => {
    switch (variant) {
      case 'success':
        return 'pi pi-check';
      case 'warning':
        return 'pi pi-exclamation-triangle';
      case 'danger':
        return 'pi pi-times';
      default:
        return 'pi pi-info-circle';
    }
  };

  return (
    <div className="dialog-overlay" onClick={onCancel} onKeyDown={handleKeyPress}>
      <div className={`dialog dialog-${variant}`} onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <div className={`dialog-icon dialog-icon-${variant}`}>
            <i className={getIcon()}></i>
          </div>
          <h2>{title}</h2>
        </div>
        
        <div className="dialog-body">
          <p>{message}</p>
          
          {type === 'prompt' && (
            <input
              type="text"
              className="dialog-input"
              placeholder={placeholder}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleConfirm()}
              autoFocus
            />
          )}
        </div>
        
        <div className="dialog-footer">
          {type !== 'alert' && (
            <Button
              type="button"
              label={cancelText}
              icon="pi pi-times"
              className="cancel-btn p-button-secondary"
              onClick={onCancel}
            />
          )}
          <Button
            type="button"
            label={confirmText}
            icon={variant === 'danger' ? 'pi pi-trash' : 'pi pi-check'}
            className={variant === 'danger' ? 'p-button-danger' : 'submit-btn'}
            onClick={handleConfirm}
            autoFocus={type === 'alert'}
          />
        </div>
      </div>
    </div>
  );
};

export default Dialog;
